import React, {ChangeEvent, createRef, ReactNode, RefObject} from "react";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import Translate from "../translation/Translate";
import FunctionHelper from "../helper/FunctionHelper";
import Icon from "./Icon";
import PortalPureComponent from "./PortalPureComponent";

/**
 * A simplified file representation
 */
export interface File {
	name: string;
	mimetype: string;
	value: string;
}

/**
 * The properties for a {@link FileInput} component
 */
interface FileInputProperties {
	inputId?: string;
	onChange?: (files: Array<File>) => void;
	filename?: string;
	value?: string;
	accept?: string;
	multiple?: boolean;
	disabled?: boolean;
}

/**
 * The state for a {@link FileInput} component
 */
interface FileInputState {
	loading: boolean,
}

/**
 * This component wraps a {@link input} of type "file" and makes it customizable
 */
export default class FileInput extends PortalPureComponent<FileInputProperties, FileInputState> {
	public fileInput: RefObject<HTMLInputElement>;

	/**
	 * Initialize an {@link FileInput} component
	 */
	constructor(props: Readonly<FileInputProperties>) {
		super(props);

		this.state = {
			loading: false
		};

		this.fileInput = createRef<HTMLInputElement>();
		this.onChange = this.onChange.bind(this);
	}

	/**
	 * show the browser file selection dialog
	 */
	public showFileDialog(): void {
		this.fileInput.current?.click()
	}

	/**
	 * Called when the input value changes and calls the given onChange function from props
	 */
	private async onChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
		if (!FunctionHelper.exists(event.target.files)) {
			return;
		}


		this.setState({
			loading: true
		}, async () => {
			let files: Array<File> = [];

			for (let file of event.target.files!) {
				files.push(
					await new Promise(function (resolve) {
						let fileReader: FileReader = new FileReader();

						fileReader.onload = function () {
							if (typeof fileReader.result !== "string") {
								return;
							}

							let filedata: string = fileReader.result.substring(fileReader.result.indexOf(',') + 1);
							let filetype: string = file.type;
							let filename: string = file.name;
							filename = (filename.indexOf('\\') === -1) ?
								filename : filename.substring(filename.lastIndexOf('\\') + 1);

							resolve({
								name: filename,
								mimetype: filetype,
								value: filedata
							} as File);
						};

						fileReader.readAsDataURL(file);
					})
				);
			}

			if (FunctionHelper.exists(this.props.onChange)) {
				this.props.onChange!(files);
			}

			this.setState({
				loading: false
			});
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let disabled: boolean | undefined = FunctionHelper.applyValue(this.props.disabled, this.state.loading);

		return (
			<div className="p-inputgroup input-file" data-control="input-file">
				{this.state.loading &&
					<div className="loader">
						<Icon icon="icon-spinner spin"/>
					</div>
				}
				<input accept={this.props.accept} type="file" ref={this.fileInput} id={this.props.inputId}
					   multiple={this.props.multiple} title={""} onChange={this.onChange}
					   disabled={disabled}/>
				<InputText value={this.props.filename} onFocus={() => this.showFileDialog()}
						   disabled={disabled}/>
				<Button className="p-inputgroup-addon" onFocus={() => this.showFileDialog()}
						icon="icon icon-upload small" tooltip={Translate.value("buttons.browse")}
						disabled={disabled}/>
			</div>
		);
	}
}